import React from 'react';
import classnames from 'classnames';
import gql from 'graphql-tag';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import { find, reject } from 'lodash';

import { Logger } from 'services';
import { refreshMembersQuery } from 'services/apollo/queries';
import { useTranslation } from 'hooks/useTranslation';
import {
  DashboardHeading,
  PopupButton,
  WebCopy,
  Loader,
  Modal,
  DefaultModalContent,
} from 'components';
import { AddMember, MemberEntry } from './';
import Icon from 'assets/icons/';

import styles from './Members.module.scss';

interface MembersData {
  me: {
    userProfile: User;
    organization: Organization;
  };
}

const getMembersTabQuery = gql`
  query getMembersTab {
    me {
      userProfile {
        id
        email
      }
      organization {
        id
        url
        name
        signupDomain
        members {
          id
          email
          name
          avatarUrl
          isAdmin
        }
      }
    }
  }
`;

const updateIsOrgAdminMutation = gql`
  mutation updateOrgAdmin($admin: Boolean!, $memberId: String!) {
    updateOrgAdmin(admin: $admin, memberId: $memberId) {
      ok
      error
    }
  }
`;

const deleteOrgMemberMutation = gql`
  mutation removeOrgMember($memberId: String!) {
    removeOrgMember(memberId: $memberId) {
      ok
      error
    }
  }
`;

export const Members = () => {
  const { t } = useTranslation();
  const client = useApolloClient();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [memberToRemove, setMemberToRemove] = React.useState<User>();

  const [updateOrgAdmin] = useMutation(updateIsOrgAdminMutation);
  const [removeOrgMember] = useMutation(deleteOrgMemberMutation);
  const { error, data } = useQuery<MembersData>(getMembersTabQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <WebCopy type="Error">{t('settings-screen.members.error')}</WebCopy>;

  const {
    me: {
      userProfile,
      organization: { url, signupDomain, members },
    },
  } = data;

  const userEntry = find(members, { email: userProfile.email });
  const otherMembers = reject(members, { email: userProfile.email });

  /**
   * Removes a member from an organization
   */
  const confirmMemberRemove = () => {
    if (!memberToRemove) {
      return;
    }

    removeOrgMember({ variables: { memberId: memberToRemove!.id } }).then((data: any) => {
      const {
        data: {
          removeOrgMember: { error, ok },
        },
      } = data;

      if (error) {
        // TODO Add error logging
        Logger.log(error);
      }

      if (ok) {
        client
          .query({
            query: refreshMembersQuery,
            fetchPolicy: 'network-only',
          })
          .catch((error) => {
            Logger.error(error);
          });
      }

      setModalOpen(false);
    });
  };

  const handleUserAction = (option: any, user: any) => {
    switch (option.key) {
      case 'make_admin':
      case 'make_member':
        const admin = option.key === 'make_admin';
        updateOrgAdmin({ variables: { admin: admin, memberId: user.id } }).then((data: any) => {
          const {
            data: {
              updateOrgAdmin: { error, ok },
            },
          } = data;

          if (error) {
            // TODO Add error logging
            Logger.log(error);
          }

          if (ok) {
            client
              .query({
                query: refreshMembersQuery,
                fetchPolicy: 'network-only',
              })
              .catch((error) => {
                Logger.error(error);
              });
          }
        });
        break;

      case 'delete':
        setMemberToRemove(user);
        setModalOpen(true);
        break;

      default:
        break;
    }
  };

  const actionLabel = (label: string, checked = false) => {
    return (
      <div className={styles.menuLabel}>
        <div className={styles.menuLabelText}>{label}</div>

        <div className={styles.checked}>{checked && <Icon.Checked />}</div>
      </div>
    );
  };

  const actionsForUser = (admin: any) => {
    return [
      {
        key: 'make_admin',
        selected: admin,
        label: t('settings-screen.members.actions.admin'),
        value: actionLabel(t('settings-screen.members.actions.admin'), admin),
      },
      {
        key: 'make_member',
        selected: !admin,
        label: t('settings-screen.members.actions.member'),
        value: actionLabel(t('settings-screen.members.actions.member'), !admin),
      },
      {
        key: 'delete',
        value: t('settings-screen.members.actions.remove'),
      },
    ];
  };

  return (
    <div className={styles.container}>
      <DashboardHeading className={styles.heading} level={1}>
        {t('settings-screen.members.members')} ({members.length})
        <PopupButton
          buttonActiveClass={styles.active}
          buttonClassName={classnames('hasTooltip tooltipBelowCenter', styles.addMember)}
          buttonProps={{ type: 'Plain' }}
          id="members"
          label={<Icon.AddPerson />}
          popupClassName={styles.addMemberWindow}
          tooltipText={t('settings-screen.members.add-member')}
        >
          <AddMember organizationUrl={url || ''} signupDomain={signupDomain || ''} />
        </PopupButton>
      </DashboardHeading>

      {userEntry && <MemberEntry user={userEntry} />}

      {otherMembers.map((user) => {
        return (
          <MemberEntry
            key={user.id}
            actions={actionsForUser(user.isAdmin)}
            user={user}
            onAction={handleUserAction}
          />
        );
      })}

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        className={styles.removeMemberModal}
      >
        <DefaultModalContent
          title={t('settings-screen.members.remove-modal-title')}
          subtext={t('settings-screen.members.remove-modal-text', {
            user: memberToRemove?.name || '',
            organization: data.me.organization.name,
          })}
          cancelButtonText={t('actions.cancel')}
          onCancel={() => {
            setModalOpen(false);
          }}
          actionButtonText={t('actions.confirm')}
          onAction={() => {
            confirmMemberRemove();
          }}
        />
      </Modal>
    </div>
  );
};
